import React from "react";
import PageWrapper from "components/PageWrapper";
import CartTableComponent from "components/cart/CartTableComponent";
import { useAuth } from "AuthContext";
import { ShoppingCart } from "lucide-react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { productQtyChange, getCurrencySymbol } from "utils/searchUtils";
import { getProductPrice, formatPrice } from "utils/cartUtils";

const MySwal = withReactContent(Swal);

const CartPage = () => {
  const { cart, updateCart, removeFromCart } = useAuth();

  const handleQtyChange = (index, type, product) => {
    const updateProduct = productQtyChange(type, product);
    updateCart({ index, product: updateProduct });
  };

  const handleManualQtyChange = ({ value, item, index }) => {
    const updateProduct = { ...item, required_qty: value };
    updateCart({ index, product: updateProduct });
  };

  const handleCartDelete = async (item) => {
    const { isConfirmed } = await MySwal.fire({
      title: <strong>Delete Part !</strong>,
      html: <i>Are you sure to delete {item.part_number} from the cart? </i>,
      icon: "info",
      cancelButtonText: "Cancel",
      confirmButtonText: "Yes",
      showCancelButton: true,
    });

    if (isConfirmed) {
      const index = cart.indexOf(item);
      if (index !== -1) removeFromCart(index);
    }
  };

  const commonProps = {
    handleQtyChange,
    handleManualQtyChange,
    handleCartDelete,
  };

  const mouserCartItems = cart.filter((item) => item.api_partner.toLowerCase() === "mouser");
  const digikeyCartItems = cart.filter((item) => item.api_partner.toLowerCase() === "digikey");
  const element14CartItems = cart.filter((item) => item.api_partner.toLowerCase() === "element14");
  const tmeCartItems = cart.filter((item) => item.api_partner.toLowerCase() === "tme");
  const rutronikCartItems = cart.filter((item) => item.api_partner.toLowerCase() === "rutronik");
  const tiCartItems = cart.filter((item) => item.api_partner.toLowerCase() === "ti");
  const arrowCartItems = cart.filter((item) => item.api_partner.toLowerCase() === "arrow");
  const ttiCartItems = cart.filter((item) => item.api_partner.toLowerCase() === "tti");
  const avnetCartItems = cart.filter((item) => item.api_partner.toLowerCase() === "avnet");
  const vericalCartItems = cart.filter((item) => item.api_partner.toLowerCase() === "verical");

  // Summary metrics computed from the live cart.
  const distributorCount = [
    mouserCartItems,
    digikeyCartItems,
    element14CartItems,
    tmeCartItems,
    rutronikCartItems,
    tiCartItems,
    arrowCartItems,
    ttiCartItems,
    avnetCartItems,
    vericalCartItems,
  ].filter((g) => g.length > 0).length;
  const totalUnits = cart.reduce((acc, it) => acc + (parseInt(it.required_qty, 10) || 0), 0);
  const backOrders = cart.filter(
    (it) => (parseInt(it.required_qty, 10) || 0) > (it.availability ?? 0),
  ).length;
  const grandTotal = cart.reduce(
    (acc, it) => acc + (parseInt(it.required_qty, 10) || 0) * getProductPrice(it),
    0,
  );
  const currencySymbol = getCurrencySymbol(cart[0]?.price?.currency);

  return (
    <PageWrapper>
      <div className="flex flex-col gap-4">
        {/* Page head */}
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[28px] leading-[34px] font-bold tracking-[-0.015em] text-s360-ink">
            Cart
          </h1>
          <span className="text-sm text-s360-fg3">
            Review and check out orders, grouped by distributor.
          </span>
        </div>

        {cart.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-20 gap-3">
            <span className="w-20 h-20 rounded-full bg-s360-violet-50 text-s360-violet inline-flex items-center justify-center">
              <ShoppingCart className="w-6 h-6" />
            </span>
            <h3 className="text-[22px] font-bold text-s360-ink">Your cart is empty</h3>
            <p className="text-sm text-s360-fg3 max-w-[380px]">
              Add parts from the search results or BOM Tool to build up a multi-distributor order.
            </p>
          </div>
        ) : (
          <>
            {/* Summary strip */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="bg-white rounded-md shadow-sm px-4 py-3.5 flex flex-col gap-1 border-t-[3px] border-t-s360-violet">
                <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3">
                  Distributors
                </span>
                <span className="text-[22px] leading-7 font-bold text-s360-ink tabular-nums">
                  {distributorCount}
                </span>
                <span className="text-[11px] text-s360-fg3">independent checkouts</span>
              </div>
              <div className="bg-white rounded-md shadow-sm px-4 py-3.5 flex flex-col gap-1 border-t-[3px] border-t-s360-teal">
                <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3">
                  Line items
                </span>
                <span className="text-[22px] leading-7 font-bold text-s360-ink tabular-nums">
                  {cart.length}
                </span>
                <span className="text-[11px] text-s360-fg3">
                  {totalUnits.toLocaleString()} total units
                </span>
              </div>
              <div className="bg-white rounded-md shadow-sm px-4 py-3.5 flex flex-col gap-1 border-t-[3px] border-t-s360-warning">
                <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3">
                  Back-ordered
                </span>
                <span className="text-[22px] leading-7 font-bold text-s360-ink tabular-nums">
                  {backOrders}
                </span>
                <span className="text-[11px] text-s360-fg3">lines exceed available stock</span>
              </div>
              <div className="bg-white rounded-md shadow-sm px-4 py-3.5 flex flex-col gap-1 border-t-[3px] border-t-s360-success">
                <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3">
                  Grand total
                </span>
                <span className="text-[22px] leading-7 font-bold text-s360-success tabular-nums">
                  {currencySymbol} {formatPrice(grandTotal)}
                </span>
                <span className="text-[11px] text-s360-fg3">across all distributors</span>
              </div>
            </div>

            {mouserCartItems.length > 0 && (
              <CartTableComponent type="Mouser" cartItems={mouserCartItems} {...commonProps} />
            )}
            {digikeyCartItems.length > 0 && (
              <CartTableComponent type="Digikey" cartItems={digikeyCartItems} {...commonProps} />
            )}
            {element14CartItems.length > 0 && (
              <CartTableComponent type="Element14" cartItems={element14CartItems} {...commonProps} />
            )}
            {tmeCartItems.length > 0 && (
              <CartTableComponent type="TME" cartItems={tmeCartItems} {...commonProps} />
            )}
            {rutronikCartItems.length > 0 && (
              <CartTableComponent type="Rutronik" cartItems={rutronikCartItems} {...commonProps} />
            )}
            {tiCartItems.length > 0 && (
              <CartTableComponent type="TI" cartItems={tiCartItems} {...commonProps} />
            )}
            {arrowCartItems.length > 0 && (
              <CartTableComponent type="Arrow" cartItems={arrowCartItems} {...commonProps} />
            )}
            {ttiCartItems.length > 0 && (
              <CartTableComponent type="TTI" cartItems={ttiCartItems} {...commonProps} />
            )}
            {avnetCartItems.length > 0 && (
              <CartTableComponent type="Avnet" cartItems={avnetCartItems} {...commonProps} />
            )}
            {vericalCartItems.length > 0 && (
              <CartTableComponent type="Verical" cartItems={vericalCartItems} {...commonProps} />
            )}
          </>
        )}
      </div>
    </PageWrapper>
  );
};

export default CartPage;
